import React, { useMemo } from "react";
import { getIsInWebView } from "scmp-sdk";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { useNewsletterContext } from "scmp-sdk/components/newsletter/newsletter-widget/contexts";
import { getTriggerPoint } from "scmp-sdk/components/newsletter/newsletter-widget/sign-up-newsletter/helpers";

import { useFallbackStorageValue } from "shared/hooks";

import { SignUpMatrixV1LocalStorageKey, SignUpNewsletterItemV1LocalStorageKey } from "./consts";
import { SocialType } from "./enums";
import { useSocialLogin } from "./hooks";
import EmailIcon from "./icon-email.svg";
import FacebookIcon from "./icon-facebook.svg";
import GoogleIcon from "./icon-google.svg";
import { Container, IconContainer } from "./styles";

export type Props = {
  isAgreedReceiveMarketing: boolean;
  onEmailClick: () => void;
};

export const SocialButtons: React.FunctionComponent<Props> = ({
  isAgreedReceiveMarketing,
  onEmailClick,
  ...props
}) => {
  const { campaignName, dataMatrix, newsletterItems, queryParametersForSocialButtons, variant } =
    useNewsletterContext();
  const { getSocialLoginUrl } = useSocialLogin();
  const { handleRoutePush, handleTrackGA4 } = useSdkContext();

  const { set: setSubscriptionNewsletterItem } = useFallbackStorageValue(
    SignUpNewsletterItemV1LocalStorageKey,
  );
  const { set: setSignUpMatrixV1LocalStorageKey } = useFallbackStorageValue(
    SignUpMatrixV1LocalStorageKey,
  );
  const handleSocialButtonClick = (socialType: SocialType) => {
    // Not setting these in querystring because of character limited in url
    setSubscriptionNewsletterItem(newsletterItems);
    setSignUpMatrixV1LocalStorageKey(dataMatrix);

    handleTrackGA4({
      action: "click",
      category: "nl",
      customized_parameters: {
        method: socialType,
        newsletter_name: newsletterItems[0].name ?? "",
        trigger_point: getTriggerPoint(variant),
      },
      subcategory: "sign_up",
    });

    const newsletterItemIds = newsletterItems
      .filter(item => item.entityId)
      .map(item => item.entityId);

    void handleRoutePush(
      getSocialLoginUrl(
        socialType,
        {
          campaignName,
          newsletterIds: newsletterItemIds,
          newsletterName: newsletterItems[0].name ?? "",
          subscription: "true",
          triggerPoint: getTriggerPoint(variant),
          variant,
        },
        isAgreedReceiveMarketing,
      ),
      {
        query: queryParametersForSocialButtons,
      },
    );
  };

  const isInWebView = useMemo(() => getIsInWebView(), []);

  return (
    <Container {...props}>
      <IconContainer onClick={() => handleSocialButtonClick(SocialType.Facebook)}>
        <FacebookIcon height="100%" width="22" />
      </IconContainer>
      {!isInWebView && (
        <IconContainer onClick={() => handleSocialButtonClick(SocialType.Google)}>
          <GoogleIcon height="100%" width="22" />
        </IconContainer>
      )}
      <IconContainer onClick={onEmailClick}>
        <EmailIcon height="100%" width="22" />
      </IconContainer>
    </Container>
  );
};

SocialButtons.displayName = "SocialButtons";
